/**
 * Public documentation page — How to connect sources to Mazo
 * Accessible at /how-to/sources without authentication
 */

import { useNavigate } from 'react-router-dom';

const sourceGuides = [
  {
    id: 'google-drive',
    name: 'Google Drive',
    color: '#4285F4',
    method: 'OAuth (Google Sign-In)',
    steps: [
      'Click "Add Source" on the Sources page and select Google Drive.',
      'You\'ll be redirected to Google to authorize Mazo to read your documents.',
      'Sign in with your Google account and grant access.',
      'Once redirected back, use the file picker to select which Google Docs to import.',
      'Mazo will read and extract knowledge from your selected documents.',
    ],
    notes: 'Only Google Docs are supported. Mazo reads document content but never modifies your files.',
  },
  {
    id: 'google-meet',
    name: 'Google Meet',
    color: '#00897B',
    method: 'OAuth (Google Sign-In)',
    steps: [
      'Click "Add Source" on the Sources page and select Google Meet.',
      'You\'ll be redirected to Google to authorize Mazo to read your Drive files (Meet recordings are stored in Drive).',
      'Sign in with your Google account and grant access.',
      'Once redirected back, Mazo will automatically navigate to your "Meet Recordings" folder.',
      'Select which recordings to import using the file picker.',
      'Mazo will read and extract knowledge from your selected meeting recordings.',
    ],
    notes: 'Google Meet recordings are stored in a "Meet Recordings" folder in Google Drive. The folder name may vary by language (e.g., "Grabaciones de Meet" in Spanish). If the folder is not found, you\'ll see your full Drive and can navigate manually.',
  },
  {
    id: 'notion',
    name: 'Notion',
    color: '#000000',
    method: 'Internal Connection Token',
    steps: [
      'Go to notion.so/my-integrations and click "New connection".',
      'Give it a name (e.g., "Mazo") and select your workspace.',
      'Under Capabilities, ensure "Read content" is enabled.',
      'Click "Submit" to create the connection.',
      'Copy the "Internal Connection Secret" (starts with ntn_).',
      'Important: Open each Notion page you want to import, click "..." → "Connections" → add your "Mazo" connection.',
      'In Mazo, click "Add Source" → Notion → paste your token.',
    ],
    notes: 'Pages must be explicitly shared with your connection before Mazo can access them. The token only grants access to pages you share.',
  },
  {
    id: 'tldv',
    name: 'tl;dv',
    color: '#FF4444',
    method: 'API Key',
    steps: [
      'Log in to tl;dv and go to your account settings.',
      'Navigate to the "Integrations" or "API" section.',
      'Generate a new API key or copy your existing one.',
      'In Mazo, click "Add Source" → tl;dv → paste your API key.',
      'Use the meeting picker to select which recordings to import.',
    ],
    notes: 'Mazo imports meeting transcripts and notes. Your recordings remain in tl;dv.',
  },
  {
    id: 'fathom',
    name: 'Fathom',
    color: '#6C5CE7',
    method: 'API Key',
    steps: [
      'Log in to Fathom and click the Settings icon (top right).',
      'Go to "My Settings" → "API Access".',
      'Click "Add" to create a new API client.',
      'Give it a name (e.g., "Mazo"), then click "Create API Client".',
      'Copy the generated API key immediately (it won\'t be shown again).',
      'In Mazo, click "Add Source" → Fathom → paste your API key.',
    ],
    notes: 'The API key can only access meetings recorded by you or shared with your team. Rate limit: 60 requests per minute.',
  },
  {
    id: 'gong',
    name: 'Gong',
    color: '#7C3AED',
    method: 'Access Key + Secret',
    steps: [
      'Log in to Gong as an admin user.',
      'Go to "Company Settings" → "API" section.',
      'Click "Get API Key" to generate credentials.',
      'You\'ll receive two values: an Access Key and an Access Key Secret.',
      'Save both immediately — the secret is shown only once.',
      'In Mazo, click "Add Source" → Gong → enter both the Access Key and Access Key Secret.',
    ],
    notes: 'Only admin users can generate API keys. The API uses Basic Authentication with both the access key and secret.',
  },
  {
    id: 'fireflies',
    name: 'Fireflies.ai',
    color: '#7C3AED',
    method: 'API Key',
    steps: [
      'Log in to Fireflies.ai.',
      'Go to "Settings" → "API".',
      'Find the "Fireflies API" section.',
      'Copy your API key.',
      'In Mazo, click "Add Source" → Fireflies.ai → paste your API key.',
    ],
    notes: 'API access requires a Fireflies Business plan or above. Free and Pro plans do not include API access.',
  },
  {
    id: 'avoma',
    name: 'Avoma',
    color: '#00B27F',
    method: 'API Key',
    steps: [
      'Log in to Avoma as an admin user.',
      'Go to "Settings" → "Organization" → "Developer".',
      'Or navigate directly to app.avoma.com/settings/developer.',
      'Click "Create API Key".',
      'Copy the key immediately and store it securely.',
      'In Mazo, click "Add Source" → Avoma → paste your API key.',
    ],
    notes: 'Only administrators can create and manage API keys. If a key is compromised, delete it and create a new one.',
  },
  {
    id: 'otter',
    name: 'Otter.ai',
    color: '#0369A1',
    method: 'OAuth (Otter Sign-In)',
    steps: [
      'Click "Add Source" on the Sources page and select Otter.ai.',
      'You\'ll be redirected to Otter.ai to authorize Mazo to access your meeting transcriptions.',
      'Sign in with your Otter.ai account and grant access.',
      'Once redirected back, use the meeting picker to select which transcriptions to import.',
      'Mazo will read and extract knowledge from your meeting transcripts.',
    ],
    notes: 'Mazo reads your transcriptions but never modifies them. Your recordings remain in Otter.ai.',
  },
  {
    id: 'granola',
    name: 'Granola',
    color: '#D97706',
    method: 'OAuth (Granola Sign-In)',
    steps: [
      'Click "Add Source" on the Sources page and select Granola.',
      'You\'ll be redirected to Granola to authorize Mazo to access your meeting notes.',
      'Sign in with your Granola account and grant access.',
      'Once redirected back, use the meeting picker to select which notes to import.',
      'Mazo will read and extract knowledge from your meeting notes and transcripts.',
    ],
    notes: 'Works with all Granola plans (free, Basic, and Business). Mazo reads your notes but never modifies them.',
  },
  {
    id: 'zoom',
    name: 'Zoom',
    color: '#2D8CFF',
    method: 'Access Token',
    steps: [
      'Go to the Zoom App Marketplace (marketplace.zoom.us) and sign in.',
      'Create a Server-to-Server OAuth app or generate a personal access token.',
      'Grant the required scopes: "cloud_recording:read:list_user_recordings" and "cloud_recording:read:list_recording_files".',
      'Copy the generated access token.',
      'In Mazo, click "Add Source" → Zoom → paste your access token.',
      'Use the recording picker to select which meeting transcripts to import.',
    ],
    notes: 'Zoom Cloud Recording must be enabled in your account settings. Only meetings with cloud recordings and transcripts can be imported. Free Zoom plans do not include cloud recording.',
  },
  {
    id: 'manual',
    name: 'Manual Entry',
    color: '#6B7280',
    method: 'Direct Input',
    steps: [
      'On the Sources page, click "Add Source" → Manual Entry.',
      'Give your note a title.',
      'Write or paste your content (Markdown is supported).',
      'Click "Create Note" to save.',
      'Your note will be processed and knowledge will be extracted automatically.',
    ],
    notes: 'Great for pasting meeting notes, blog drafts, or any text content you want Mazo to learn from.',
  },
];

const comingSoon = [
  { name: 'Lindy.ai', reason: 'Lindy uses a push-based workflow model — we\'re exploring webhook support.' },
  { name: 'Slack', reason: 'Full conversation import with topic filtering.' },
];

export function HowToSourcesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img src="/logo.svg" alt="Mazo" className="w-7 h-7" />
            <span style={{ fontFamily: "'Outfit', sans-serif" }} className="font-medium text-xl text-gray-900">
              Mazo
            </span>
          </div>
          <button
            onClick={() => navigate('/sources')}
            className="text-sm px-4 py-2 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 transition-colors"
          >
            Go to Sources
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Connecting Sources to Mazo
          </h1>
          <p className="text-lg text-gray-600">
            Step-by-step guides for connecting your knowledge sources. Mazo reads your content and extracts insights to generate LinkedIn posts.
          </p>
        </div>

        <div className="space-y-8">
          {sourceGuides.map((guide) => (
            <section
              key={guide.id}
              id={guide.id}
              className="bg-white rounded-xl border p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: guide.color }}
                >
                  {guide.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{guide.name}</h2>
                  <p className="text-sm text-gray-500">
                    Connection method: <span className="font-medium text-gray-700">{guide.method}</span>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">Steps</h3>
                <ol className="space-y-3">
                  {guide.steps.map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-mazo-100 text-mazo-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 text-sm leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {guide.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Note: </span>
                    {guide.notes}
                  </p>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Coming Soon */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Coming Soon
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {comingSoon.map((item) => (
              <div key={item.name} className="bg-white rounded-xl border p-5">
                <h3 className="font-medium text-gray-900 mb-1">{item.name}</h3>
                <p className="text-sm text-gray-500">{item.reason}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t text-center">
          <p className="text-sm text-gray-400">
            Need help?{' '}
            <a href="mailto:support@getmazo.ai" className="text-mazo-500 hover:text-mazo-600">
              Contact support
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
